import { H3 } from '@h3/types';
import * as Api from '../api';

export default class OrganizationService {
    /**
     * 根据ID和范围获取组织信息
     * @param ids ID数组
     * @param range 范围数组(无范围限定时会从缓存获取数据)
     */
    static getOrgInfoByIdsWithRange(ids: string[], range?: string[]): H3.Org.IOrgInfo[] | Promise<H3.Org.IOrgInfo[]> {
        if (!range || !range.length) {
            return window.h3Daf.Cache.getOrgInfoByIds(ids);
        }

        return Api.Org.getOrgList({
            ids,
            range,
        })
            .then((res) => {
                if (!res.successful) {
                    throw new Error();
                }
                const { map } = this._mergeResp(res.returnData);
                return ids.map((id) => map[id]).filter(Boolean);
            })
            .catch(() => {
                return [];
            });
    }

    /**
     * 搜索组织机构信息
     * @param keyword 搜索关键字
     * @param pageIndex 分页序号
     * @param pageSize 分页大小
     * @param orgTypes 搜索类型
     * @param showUnActive 是否显示离职人员
     * @param orgScope 搜索范围
     * @param range 限定范围
     */
    static async searchOrgInfo(
        keyword: string,
        pageIndex: number,
        pageSize: number,
        orgTypes: H3.Org.UnitType[],
        showUnActive: boolean,
        orgScope: H3.Org.OrgScopeType,
        range?: string[],
    ) {
        try {
            const res = await Api.Org.searchOrgList({
                keyword,
                pageIndex,
                pageSize,
                orgTypes,
                showUnActive,
                orgScope,
                range,
            });

            if (!res.successful) {
                throw new Error();
            }

            const { users, departments, roles } = this._mergeResp(res.returnData);
            const data = [];

            if (orgTypes.includes(H3.Org.UnitType.User)) {
                data.push(...users);
            }
            if (orgTypes.includes(H3.Org.UnitType.OrganizationUnit)) {
                data.push(...departments);
            }
            if (orgTypes.includes(H3.Org.UnitType.Role)) {
                data.push(...roles);
            }
            return {
                data,
                hasMore: res.returnData.hasMore,
            };
        } catch {
            return { data: [], hasMore: false };
        }
    }

    /**
     * 根据id获取指定部门/角色及其子部门/角色
     * @param params 指定ids
     */
    static async getCurAndSubByIds(ids: string[]) {
        try {
            const res = await Api.Org.getOrgChild({ ids });
            if (!res.successful) {
                throw new Error();
            }
            return this._mergeResp(res.returnData).all;
        } catch {
            return [];
        }
    }

    /**
     * 获取企业根部门
     * @param getInner 是否获取内部部门，默认为true
     * @param getExternal 是否获取外部组织，默认为true
     * @param addExternalRoot 是否为所有外部组织添加一个外部组织根节点， 默认为true
     * @param externalOnlyIncludeCurrentUser 是否只返回包含当前用户的外部组织，默认为true
     */
    static async getRootDepartments(
        getInner = true,
        getExternal = true,
        addExternalRoot = true,
        externalOnlyIncludeCurrentUser = true,
    ) {
        const innerDepartments: H3.Org.IOrgDepartmentInfo[] = [];
        const externalDepartments: H3.Org.IOrgDepartmentInfo[] = [];

        if (getInner) {
            try {
                const userInfo = window.h3Daf.EngineContext.userInfo;
                const companyId = userInfo?.currentEngine?.companyId || '';

                const res = await Api.Org.getOrgDepartmentList({
                    departmentId: companyId,
                });

                if (!res.successful) {
                    throw new Error();
                }

                const root = {
                    id: companyId,
                    parentId: '',
                    name: userInfo?.currentEngine?.engineName || '',
                    hasChild: true,
                    state: -1,
                    isExternal: false,
                    entryId: '',
                    unitType: H3.Org.UnitType.OrganizationUnit,
                    nodeType: H3.Org.OrgNodeType.Unit,
                    type: H3.Org.WeChartOrgType.Department,
                };

                const children =
                    res.returnData
                        .map((item) => ({
                            ...item,
                            parentId: root.id,
                            unitType: H3.Org.UnitType.OrganizationUnit,
                            nodeType: H3.Org.OrgNodeType.Unit,
                            type: H3.Org.WeChartOrgType.Department,
                        }))
                        .filter((item) => item.parentId !== '') ?? [];

                innerDepartments.push(root, ...children);
            } catch {
                // catch error
            }
        }

        if (getExternal) {
            try {
                const res = await Api.Org.getOrgAssociationList({
                    onlyIncludeCurrentUser: externalOnlyIncludeCurrentUser,
                });

                if (!res.successful || res.returnData.length === 0) {
                    throw new Error();
                }

                const children = res.returnData.map((item) => ({
                    ...item,
                    unitType: H3.Org.UnitType.OrganizationUnit,
                    nodeType: H3.Org.OrgNodeType.Unit,
                    type: H3.Org.WeChartOrgType.Department,
                }));

                if (!addExternalRoot) {
                    externalDepartments.push(...children);
                } else {
                    const root = {
                        id: H3.Util.EXTERNAL_ROOT_ID,
                        parentId: '',
                        name: '外部组织',
                        hasChild: true,
                        state: 0,
                        isExternal: true,
                        entryId: '',
                        selectable: false,
                        unitType: H3.Org.UnitType.OrganizationUnit,
                        nodeType: H3.Org.OrgNodeType.Unit,
                        type: H3.Org.WeChartOrgType.Department,
                    };
                    externalDepartments.push(
                        root,
                        ...children.map((child) => ({
                            ...child,
                            parentId: root.id,
                        })),
                    );
                }
            } catch {
                // catch error
            }
        }

        return [...innerDepartments, ...externalDepartments];
    }

    /**
     * 获取给定部门id下的子部门
     * @param parentId 父部门id
     * @param range 范围限定
     */
    static async getSubDepartmentByParentId(parentId: string, range?: string[]) {
        try {
            const res = await Api.Org.getOrgDepartmentList({
                departmentId: parentId,
                range: range, // TODO 这个字段有没有作用
                showExternal: false, // TODO 这个字段是否有作用
            });

            if (!res.successful) {
                throw new Error();
            }

            return res.returnData.map((item) => ({
                ...item,
                unitType: H3.Org.UnitType.OrganizationUnit,
                nodeType: H3.Org.OrgNodeType.Unit,
                type: H3.Org.WeChartOrgType.Department,
            }));
        } catch {
            return [];
        }
    }

    /**
     * 请求给定部门下人员
     * @param departmentId 部门id
     * @param showUnActive 是否显示离职人员
     * @param range 范围限定
     */
    static async getUserByDepartmentId(departmentId: string, showUnActive: boolean, range?: string[]) {
        try {
            const res = await Api.Org.getOrgUserList({
                parentId: departmentId,
                showUnActive,
                range,
            });
            if (!res.successful) {
                throw new Error();
            }
            return res.returnData.map((item) => ({
                ...item,
                type: H3.Org.WeChartOrgType.User,
                nodeType: H3.Org.OrgNodeType.Unit,
                unitType: H3.Org.UnitType.User,
            }));
        } catch {
            return [];
        }
    }

    /** 获取企业角色列表 */
    static async getRoles(): Promise<H3.Org.IOrgInfo[]> {
        try {
            const res = await Api.Org.getOrgRoleList();

            if (!res.successful) {
                return [];
            }

            return res.returnData.map((item) => ({
                ...item,
                unitType: H3.Org.UnitType.Role,
                nodeType: H3.Org.OrgNodeType.Unit,
            }));
        } catch {
            return [];
        }
    }

    /** 根据id获取组织机构信息（用于列表展示，处出于性能考虑，单独接口） */
    static async getOrgDetailById(id: string) {
        const res = await Api.Org.getOrgInfo({ unitId: id });
        if (!res.successful) {
            return null;
        }
        if (res.returnData.unitType === H3.Org.UnitType.User) {
            return {
                type: H3.Org.WeChartOrgType.User,
                data: res.returnData.user,
            };
        } else {
            return {
                type: H3.Org.WeChartOrgType.Department,
                data: res.returnData.department,
            };
        }
    }

    private static _mergeResp(resp: H3.Org.ISearchOrgListModel) {
        const map = {} as Record<string, H3.Org.IOrgInfo>;

        const users: H3.Org.IOrgInfo[] = [];
        const departments: H3.Org.IOrgInfo[] = [];
        const roles: H3.Org.IOrgInfo[] = [];

        resp.users.forEach((user) => {
            const _user = {
                ...user,
                unitType: H3.Org.UnitType.User,
                nodeType: H3.Org.OrgNodeType.Unit,
                type: H3.Org.WeChartOrgType.User,
            };
            map[user.id] = _user;
            users.push(_user);
        });
        resp.departments.forEach((department) => {
            const _department = {
                ...department,
                unitType: H3.Org.UnitType.OrganizationUnit,
                nodeType: H3.Org.OrgNodeType.Unit,
                type: H3.Org.WeChartOrgType.Department,
            };
            map[department.id] = _department;
            departments.push(_department);
        });
        resp.roles.forEach((role) => {
            const _role = {
                ...role,
                unitType: H3.Org.UnitType.Role,
                nodeType: H3.Org.OrgNodeType.Unit,
            };
            map[role.id] = _role;
            roles.push(_role);
        });

        return {
            map,
            users,
            departments,
            roles,
            all: [...users, ...departments, ...roles],
        };
    }
}
